import React, {
    forwardRef, useContext,
    useEffect,
    useImperativeHandle,
    useState
} from 'react';
import {Col, Form, FormInstance, Row, Space, theme, Collapse} from "antd";
import styled from 'styled-components'
import {Prompt} from "../../components/promptBlocker/promptBlocker";
import {observer} from "mobx-react";
import {StoreContext} from "../../store.context";
import {CollapseStyled} from "../../components/styled/collapse.styled";
import {useFormEventListener} from "../../provider/event/event.context.provider";
import {FormatHandler} from "../../handler/format.handler";

const { Panel } = Collapse;

const StyleWrapper = styled.div`
    button + button {
        margin-left: 8px;
    }
    
    label {
        height: unset!important;
    }
`

const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 16 },
    style: { padding: 16 }
};

export const BuildForm: React.FC<any> = observer(forwardRef((props, ref) => {
    const { token } = theme.useToken();
    const { appStore: { translate }} = useContext(StoreContext);
    const { notify } = useFormEventListener();

    const formReference = React.createRef<FormInstance>();
    const [ formElements, setFormElements ] = useState<any>(props.items ?? []);
    const [ formStateDirty, setFormStateDirty] = useState<boolean>(false);

    useImperativeHandle(ref, () => ({
        getForm: (): FormInstance | null => {
            return formReference.current;
        },
        submitForm: async () => {
            //we need to reset the form errors after submit press, for mixed server/client field validations
            formReference.current?.setFields(
                formReference.current?.getFieldsError().map((item) => ({
                    name: item.name,
                    errors: []
                }))
            )

            if (props.formErrors) {
                formReference.current?.setFields(
                    Object.keys(props.formErrors).map(fieldName => ({
                        name: fieldName,
                        errors: []
                    }))
                )
            }
            try {
                await formReference.current?.validateFields();

                //if at least one error in form, we do nothing
                if (formReference.current?.getFieldsError().some((item) => item.errors.length > 0)) {
                    return;
                }

                setFormStateDirty(false);

                const values = FormatHandler.removeEmptyStrings(formReference.current?.getFieldsValue());

                props.onFinish(values);
            } catch (e) {}
        },
        resetForm: () => {
            formReference.current?.resetFields();
        },
        setData: (values: any) => {
            formReference.current?.setFieldsValue(values);
            notify(values, formReference.current?.getFieldsValue())
        },

        reValidateFields: (fields:[string], validationOptions: any = {recursive: true, dirty: false, validateOnly: false}) => {
            fields.forEach((item: string) => {
                try {
                    const value = formReference?.current?.getFieldValue(item.split('.'));
                    if (value) {
                        formReference?.current?.setFieldValue(item.split('.'), value);
                        formReference?.current?.validateFields(item.split('.'), validationOptions)
                    }
                } catch (e){

                }
            })
        }
    }));

    useEffect(() => {
        setFormElements(props.items)
    }, [props.items])


    useEffect(() => {
        if (!props.formErrors) return;

        formReference.current?.setFields(
            Object.keys(props.formErrors).map(fieldName => ({
                name: fieldName.split('.'),
                errors: props.formErrors[fieldName]
            }))
        )
    }, [props.formErrors, formReference]);

    const formPositions = ['left', 'right'];

    const uniqueItems = (list: any, keyFn: any) => list.reduce((resultSet: any, item: any) =>
            resultSet.add(typeof keyFn === 'string' ? item[keyFn] : keyFn(item)),
    new Set()).size;

    return (
        <StyleWrapper>
            <Form ref={formReference} {...layout} layout="vertical" style={{ padding: 0 }} onFinish={props.onFinish} onValuesChange={ (changedValues, values) => notify(changedValues, values) }>
                <Row gutter={8}>
                    {
                        formPositions.map((position, a) => {
                            let positionalElements = formElements.filter((element: any) => element.position === position);
                            if (!positionalElements.length) return null;

                            return (
                                <Col key={position} span={24 / uniqueItems(formElements, 'position')}>
                                    <Space direction={`vertical`} size="small" style={{ display: 'flex' }}>
                                        {
                                            positionalElements.map((formBlock: any, b: number) => {
                                                return (
                                                    <CollapseStyled collapsible={ props.collapsible ? "icon" : "disabled" } key={b} defaultActiveKey={`1`} style={{
                                                        background: token.colorBgContainer,
                                                    }}>
                                                        <Panel showArrow={props.collapsible ?? false} header={formBlock.title} key="1">
                                                            { formBlock.inputs.map((item: any, c: any) => {
                                                                const FormType = item ? item.type : null;

                                                                if (!FormType) return null;

                                                                return <FormType
                                                                    key={c}
                                                                    translationDomain={item.translationDomain ?? formBlock.translationDomain ?? props.translationDomain}
                                                                    choiceTranslationDomain={ item.choiceTranslationDomain }
                                                                    onClick={ item.onClick }
                                                                    name={ item.name }
                                                                    label={ item.label }
                                                                    rules={ item.constraints }
                                                                    choices={ item.choices }
                                                                    multiple={ item.multiple }
                                                                    vertical={ item.vertical }
                                                                    disabled={ item.disabled }
                                                                    expanded={ item.expanded }
                                                                    firstFieldName={ item.firstFieldName }
                                                                    secondFieldName={ item.secondFieldName }
                                                                    firstLabel={ item.firstLabel }
                                                                    secondLabel={ item.secondLabel }
                                                                    firstFieldConstraints={ item.firstFieldConstraints }
                                                                    secondFieldConstraints={ item.secondFieldConstraints }
                                                                    validateTrigger={ item.validateTrigger }
                                                                    onChange={(event: any) => {
                                                                        setFormStateDirty(true);
                                                                        if (typeof item.onChange === 'function' ) {
                                                                            item.onChange(event)
                                                                        }
                                                                    }}
                                                                    onInput={(event: any) => {
                                                                        if (typeof item.onInput === 'function' ) {
                                                                            item.onInput(event)
                                                                        }
                                                                    }}
                                                                    loading={ item.loading }
                                                                />
                                                            }) }
                                                        </Panel>
                                                    </CollapseStyled>
                                                )
                                            })
                                        }
                                    </Space>
                                </Col>
                            )
                        })
                    }
                </Row>
            </Form>

            {
                props.dirtyStateBlocker && (
                    <Prompt
                        when={ formStateDirty }
                        message={ translate('global.unsaved_changes_leave') }
                        beforeUnload={ true }
                    />
                )
            }
        </StyleWrapper>
    )
}))